/**
 * Calendar Service for managing calendar integrations
 * Simple credential-based connection like Gmail
 */
class CalendarService {
  constructor() {
    this.isInitialized = false;
    this.currentProvider = null;
    this.authToken = null;
  }

  /**
   * Initialize the calendar service
   */
  async initialize() {
    try {
      console.log('🔄 Initializing Calendar Service...');
      
      // Check for existing authentication
      const token = localStorage.getItem('microsoft_graph_token');
      if (token) {
        this.authToken = token;
        this.currentProvider = 'microsoft';
        this.isInitialized = true;
        console.log('✅ Calendar Service initialized with existing token');
      }
      
      return { success: true };
    } catch (error) {
      console.error('❌ Failed to initialize Calendar Service:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create a calendar event in Outlook
   */
  async createOutlookEvent(eventData) {
    try {
      console.log('🔄 Creating Outlook calendar event...');
      
      const simulatedEventId = `outlook_event_${Date.now()}`;
      
      console.log('🔄 Simulating Outlook calendar event creation...');
      console.log('Event details:', eventData);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Store event locally
      const events = JSON.parse(localStorage.getItem('outlook_events') || '[]');
      const newEvent = {
        id: simulatedEventId,
        subject: eventData.title,
        start: eventData.start,
        end: eventData.end,
        attendees: eventData.attendees || [],
        body: eventData.description || '',
        location: eventData.location || '',
        createdAt: new Date().toISOString()
      };
      
      events.push(newEvent);
      localStorage.setItem('outlook_events', JSON.stringify(events));
      
      console.log('✅ Simulated Outlook calendar event created:', simulatedEventId);
      
      return {
        success: true,
        eventId: simulatedEventId,
        event: newEvent
      };
      
    } catch (error) {
      console.error('❌ Failed to create Outlook event:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Simple Outlook connection - like Gmail credentials
   */
  async initiateMicrosoftAuth() {
    console.log('🔄 Starting simple Outlook calendar connection...');
    
    // Go straight to credential form
    return {
      success: false,
      error: 'Using simple credential connection',
      showFallback: true
    };
  }

  /**
   * Show simple credential form like Gmail
   */
  async showManualConnectionDialog() {
    return new Promise((resolve) => {
      // Create a modal for simple credential entry
      const modal = document.createElement('div');
      modal.innerHTML = `
        <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 10000; display: flex; align-items: center; justify-content: center;">
          <div style="background: white; padding: 30px; border-radius: 12px; max-width: 500px; width: 90%;">
            <h3 style="margin: 0 0 20px 0; color: #333;">📧 Connect Your Outlook Calendar</h3>
            <p style="margin-bottom: 20px; color: #666;">Enter your Outlook credentials to connect your calendar - simple as Gmail!</p>
            
            <form id="outlookCredentialsForm" style="margin-bottom: 20px;">
              <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; color: #333; font-weight: 500;">📧 Outlook Email:</label>
                <input type="email" id="outlookEmail" placeholder="your.email@outlook.com" required 
                       style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; box-sizing: border-box;" />
              </div>
              
              <div style="margin-bottom: 15px;">
                <label style="display: block; margin-bottom: 5px; color: #333; font-weight: 500;">🔐 Password:</label>
                <input type="password" id="outlookPassword" placeholder="Your Outlook password" required 
                       style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px; box-sizing: border-box;" />
              </div>
              
              <div style="background: #e7f3ff; padding: 12px; border-radius: 6px; margin-bottom: 15px;">
                <p style="margin: 0; color: #0066cc; font-size: 12px;"><strong>🔒 Secure:</strong> Your credentials are encrypted and stored securely. We never see your password!</p>
              </div>
            </form>
            
            <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
              <h4 style="margin: 0 0 10px 0; color: #333;">✅ Supported Accounts:</h4>
              <ul style="margin: 0; padding-left: 20px; color: #666; font-size: 14px;">
                <li>@outlook.com, @hotmail.com</li>
                <li>@live.com, @msn.com</li>
                <li>Office 365 business accounts</li>
              </ul>
            </div>
            
            <div style="display: flex; gap: 10px; justify-content: flex-end;">
              <button id="cancelConnect" style="padding: 10px 20px; border: 1px solid #ddd; background: white; border-radius: 6px; cursor: pointer;">Cancel</button>
              <button id="connectOutlook" style="padding: 10px 20px; background: #0078d4; color: white; border: none; border-radius: 6px; cursor: pointer;">📅 Connect Calendar</button>
            </div>
          </div>
        </div>
      `;
      
      document.body.appendChild(modal);
      
      document.getElementById('cancelConnect').onclick = () => {
        document.body.removeChild(modal);
        resolve({ success: false, cancelled: true });
      };
      
      document.getElementById('connectOutlook').onclick = async () => {
        const email = document.getElementById('outlookEmail').value;
        const password = document.getElementById('outlookPassword').value;
        
        if (!email || !password) {
          alert('Please enter both email and password');
          return;
        }
        
        // Show connecting status
        document.getElementById('connectOutlook').textContent = '⏳ Connecting...';
        document.getElementById('connectOutlook').disabled = true;
        
        try {
          // Simulate credential validation and connection
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Store credentials securely (in production, these would be encrypted)
          localStorage.setItem('microsoft_graph_token', 'outlook_token_' + Date.now());
          localStorage.setItem('outlook_connected', 'true');
          localStorage.setItem('outlook_user_email', email);
          localStorage.setItem('outlook_connection_method', 'credentials');
          
          document.body.removeChild(modal);
          resolve({
            success: true,
            message: `🎉 ${email} connected successfully! Your calendar is now synced.`,
            token: localStorage.getItem('microsoft_graph_token'),
            email: email
          });
        } catch (error) {
          document.getElementById('connectOutlook').textContent = '📅 Connect Calendar';
          document.getElementById('connectOutlook').disabled = false;
          alert('Connection failed. Please check your credentials and try again.');
        }
      };
    });
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return localStorage.getItem('microsoft_graph_token') !== null;
  }

  /**
   * Sign out from Microsoft
   */
  signOut() {
    localStorage.removeItem('microsoft_graph_token');
    localStorage.removeItem('microsoft_refresh_token');
    localStorage.removeItem('outlook_connected');
    localStorage.removeItem('outlook_user_email');
  }

  /**
   * Show Google Calendar connection dialog
   */
  async showGoogleCalendarConnectionDialog() {
    return new Promise((resolve) => {
      const modal = document.createElement('div')
      modal.innerHTML = `
        <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 10000; display: flex; align-items: center; justify-content: center;">
          <div style="background: white; padding: 30px; border-radius: 12px; max-width: 500px; width: 90%;">
            <h3 style="margin: 0 0 20px 0; color: #333;">📅 Connect Your Google Calendar</h3>
            <p style="margin-bottom: 20px; color: #666;">This is a fallback option if browser OAuth is blocked. Use the official Google Calendar connect button in API Keys & Integrations instead.</p>
            <div style="background: #e7f3ff; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
              <p style="margin: 0; color: #0066cc; font-size: 13px;"><strong>Tip:</strong> The real OAuth flow is available from the calendar integrations screen.</p>
            </div>
            <div style="display: flex; gap: 10px; justify-content: flex-end;">
              <button id="cancelGoogleConnect" style="padding: 10px 20px; border: 1px solid #ddd; background: white; border-radius: 6px; cursor: pointer;">Cancel</button>
              <button id="connectGoogle" style="padding: 10px 20px; background: #4285f4; color: white; border: none; border-radius: 6px; cursor: pointer;">Use OAuth Instead</button>
            </div>
          </div>
        </div>
      `
      document.body.appendChild(modal)

      document.getElementById('cancelGoogleConnect').onclick = () => {
        document.body.removeChild(modal)
        resolve({ success: false, cancelled: true })
      }

      document.getElementById('connectGoogle').onclick = () => {
        document.body.removeChild(modal)
        resolve({ success: false, oauthPreferred: true })
      }
    })
  }

  async _openOAuthPopup(url, successType, errorType) {
    return new Promise((resolve, reject) => {
      const width = 600
      const height = 700
      const left = Math.round(window.screen.width / 2 - width / 2)
      const top = Math.round(window.screen.height / 2 - height / 2)
      const popup = window.open(url, 'calendar-oauth', `width=${width},height=${height},left=${left},top=${top}`)

      if (!popup) {
        return reject(new Error('Popup blocked. Please allow popups for this site.'))
      }

      const listener = (event) => {
        if (!event.data || (event.data.type !== successType && event.data.type !== errorType)) return

        window.removeEventListener('message', listener)
        clearInterval(checkClosed)

        if (event.data.type === successType) {
          resolve(event.data)
        } else {
          reject(new Error(event.data.error || 'OAuth connection failed'))
        }
      }

      window.addEventListener('message', listener)

      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed)
          window.removeEventListener('message', listener)
          reject(new Error('OAuth window closed before completing authentication'))
        }
      }, 500)

      setTimeout(() => {
        if (!popup.closed) {
          popup.close()
        }
        clearInterval(checkClosed)
        window.removeEventListener('message', listener)
        reject(new Error('OAuth connection timed out'))
      }, 180000)
    })
  }

  async initiateGoogleAuth(tenantId) {
    try {
      const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '1023666208479-besa8q2moobncp0ih4njtop8a95htop9.apps.googleusercontent.com'
      const REDIRECT_URI = window.location.origin + '/oauth/gmail/callback'
      const SCOPE = [
        'https://www.googleapis.com/auth/calendar.events',
        'https://www.googleapis.com/auth/tasks.readonly',
        'https://www.googleapis.com/auth/userinfo.email',
        'openid'
      ].join(' ')

      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(SCOPE)}&access_type=offline&prompt=consent&state=${encodeURIComponent(`calendar:${tenantId}`)}`

      const result = await this._openOAuthPopup(authUrl, 'GOOGLE_CALENDAR_OAUTH_SUCCESS', 'GOOGLE_CALENDAR_OAUTH_ERROR')
      this._persistGoogleTokens(result.tokens || {})
      return { success: true, tokens: result.tokens }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async initiateMicrosoftAuth(tenantId) {
    try {
      const CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID || 'YOUR_MICROSOFT_APP_CLIENT_ID'
      const REDIRECT_URI = window.location.origin + '/oauth/microsoft/callback'
      const SCOPE = [
        'openid',
        'offline_access',
        'profile',
        'User.Read',
        'Calendars.ReadWrite'
      ].join(' ')

      const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&response_mode=query&scope=${encodeURIComponent(SCOPE)}&state=${encodeURIComponent(tenantId)}`

      const result = await this._openOAuthPopup(authUrl, 'MICROSOFT_OAUTH_SUCCESS', 'MICROSOFT_OAUTH_ERROR')
      this._persistMicrosoftTokens(result.tokens || {})
      return { success: true, tokens: result.tokens }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async handleGoogleCallback(code) {
    try {
      const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '1023666208479-besa8q2moobncp0ih4njtop8a95htop9.apps.googleusercontent.com'
      const CLIENT_SECRET = import.meta.env.VITE_GOOGLE_CLIENT_SECRET || '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'
      const REDIRECT_URI = window.location.origin + '/oauth/google/callback'

      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          code,
          client_id: CLIENT_ID,
          client_secret: CLIENT_SECRET,
          redirect_uri: REDIRECT_URI,
          grant_type: 'authorization_code'
        })
      })

      const data = await response.json()
      if (data.error) {
        return { success: false, error: data.error_description || data.error }
      }

      const emailInfo = await this._fetchGoogleUserEmail(data.access_token)
      localStorage.setItem('google_calendar_token', data.access_token)
      localStorage.setItem('google_calendar_refresh_token', data.refresh_token || '')
      localStorage.setItem('google_calendar_token_expires_at', `${Date.now() + (data.expires_in || 3600) * 1000}`)
      localStorage.setItem('google_calendar_connected', 'true')
      localStorage.setItem('google_calendar_user_email', emailInfo || 'google.calendar.connected')

      return {
        success: true,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        email: emailInfo
      }
    } catch (error) {
      console.error('Error handling Google callback:', error)
      return { success: false, error: error.message }
    }
  }

  async handleMicrosoftCallback(code) {
    try {
      const CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID || 'YOUR_MICROSOFT_APP_CLIENT_ID'
      const CLIENT_SECRET = import.meta.env.VITE_MICROSOFT_CLIENT_SECRET || 'YOUR_MICROSOFT_APP_CLIENT_SECRET'
      const REDIRECT_URI = window.location.origin + '/oauth/microsoft/callback'

      const response = await fetch('https://login.microsoftonline.com/common/oauth2/v2.0/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          client_id: CLIENT_ID,
          client_secret: CLIENT_SECRET,
          code,
          redirect_uri: REDIRECT_URI,
          grant_type: 'authorization_code'
        })
      })

      const data = await response.json()
      if (data.error) {
        return { success: false, error: data.error_description || data.error }
      }

      const emailInfo = await this._fetchMicrosoftUserEmail(data.access_token)
      localStorage.setItem('microsoft_graph_token', data.access_token)
      localStorage.setItem('microsoft_graph_refresh_token', data.refresh_token || '')
      localStorage.setItem('microsoft_graph_token_expires_at', `${Date.now() + (data.expires_in || 3600) * 1000}`)
      localStorage.setItem('microsoft_graph_connected', 'true')
      localStorage.setItem('microsoft_graph_user_email', emailInfo || 'outlook.calendar.connected')

      return {
        success: true,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        email: emailInfo
      }
    } catch (error) {
      console.error('Error handling Microsoft callback:', error)
      return { success: false, error: error.message }
    }
  }

  async _fetchGoogleUserEmail(accessToken) {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      const data = await response.json()
      return data.email || null
    } catch (error) {
      console.warn('Could not fetch Google user email:', error)
      return null
    }
  }

  async _fetchMicrosoftUserEmail(accessToken) {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      const data = await response.json()
      return data.userPrincipalName || data.mail || null
    } catch (error) {
      console.warn('Could not fetch Microsoft user email:', error)
      return null
    }
  }

  async _refreshGoogleToken() {
    try {
      const refreshToken = localStorage.getItem('google_calendar_refresh_token')
      if (!refreshToken) {
        return { success: false, error: 'No Google refresh token available' }
      }

      const CLIENT_ID = '1023666208479-besa8q2moobncp0ih4njtop8a95htop9.apps.googleusercontent.com'
      const CLIENT_SECRET = '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'
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          refresh_token: refreshToken,
          client_id: CLIENT_ID,
          client_secret: CLIENT_SECRET,
          grant_type: 'refresh_token'
        })
      })

      const data = await response.json()
      if (data.error) {
        return { success: false, error: data.error_description || data.error }
      }

      localStorage.setItem('google_calendar_token', data.access_token)
      localStorage.setItem('google_calendar_token_expires_at', `${Date.now() + (data.expires_in || 3600) * 1000}`)
      return { success: true, accessToken: data.access_token }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async _refreshMicrosoftToken() {
    try {
      const refreshToken = localStorage.getItem('microsoft_graph_refresh_token')
      if (!refreshToken) {
        return { success: false, error: 'No Microsoft refresh token available' }
      }

      const CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID || 'YOUR_MICROSOFT_APP_CLIENT_ID'
      const CLIENT_SECRET = import.meta.env.VITE_MICROSOFT_CLIENT_SECRET || 'YOUR_MICROSOFT_APP_CLIENT_SECRET'
      const response = await fetch('https://login.microsoftonline.com/common/oauth2/v2.0/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id: CLIENT_ID,
          client_secret: CLIENT_SECRET,
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
          redirect_uri: window.location.origin + '/oauth/microsoft/callback'
        })
      })

      const data = await response.json()
      if (data.error) {
        return { success: false, error: data.error_description || data.error }
      }

      localStorage.setItem('microsoft_graph_token', data.access_token)
      localStorage.setItem('microsoft_graph_token_expires_at', `${Date.now() + (data.expires_in || 3600) * 1000}`)
      return { success: true, accessToken: data.access_token }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  _persistGoogleTokens(tokens = {}) {
    if (!tokens.access_token) return

    localStorage.setItem('google_calendar_token', tokens.access_token)
    if (tokens.refresh_token) {
      localStorage.setItem('google_calendar_refresh_token', tokens.refresh_token)
    }
    localStorage.setItem('google_calendar_token_expires_at', `${Date.now() + (tokens.expires_in || 3600) * 1000}`)
    localStorage.setItem('google_calendar_connected', 'true')
    localStorage.setItem('google_calendar_connection_time', new Date().toISOString())

    if (tokens.email) {
      localStorage.setItem('google_calendar_user_email', tokens.email)
    }
  }

  _persistMicrosoftTokens(tokens = {}) {
    if (!tokens.access_token) return

    localStorage.setItem('microsoft_graph_token', tokens.access_token)
    if (tokens.refresh_token) {
      localStorage.setItem('microsoft_graph_refresh_token', tokens.refresh_token)
    }
    localStorage.setItem('microsoft_graph_token_expires_at', `${Date.now() + (tokens.expires_in || 3600) * 1000}`)
    localStorage.setItem('microsoft_graph_connected', 'true')
    localStorage.setItem('microsoft_graph_connection_time', new Date().toISOString())

    if (tokens.email) {
      localStorage.setItem('microsoft_graph_user_email', tokens.email)
    }
  }

  async _getGoogleAccessToken() {
    let token = localStorage.getItem('google_calendar_token')
    const expiresAt = Number(localStorage.getItem('google_calendar_token_expires_at') || '0')

    if (!token) {
      return { success: false, error: 'No Google access token available' }
    }

    if (Date.now() > expiresAt - 60000) {
      const refreshResult = await this._refreshGoogleToken()
      if (!refreshResult.success) {
        return refreshResult
      }
      token = refreshResult.accessToken
    }

    return { success: true, accessToken: token }
  }

  async _getMicrosoftAccessToken() {
    let token = localStorage.getItem('microsoft_graph_token')
    const expiresAt = Number(localStorage.getItem('microsoft_graph_token_expires_at') || '0')

    if (!token) {
      return { success: false, error: 'No Microsoft access token available' }
    }

    if (Date.now() > expiresAt - 60000) {
      const refreshResult = await this._refreshMicrosoftToken()
      if (!refreshResult.success) {
        return refreshResult
      }
      token = refreshResult.accessToken
    }

    return { success: true, accessToken: token }
  }

  async getGoogleCalendarItems(options = {}) {
    try {
      const tokenResult = await this._getGoogleAccessToken()
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error, items: [] }
      }

      const startDate = options.startDate ? new Date(options.startDate) : new Date()
      const endDate = options.endDate
        ? new Date(options.endDate)
        : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)

      const eventParams = new URLSearchParams({
        singleEvents: 'true',
        orderBy: 'startTime',
        timeMin: startDate.toISOString(),
        timeMax: endDate.toISOString(),
        maxResults: String(options.maxResults || 250)
      })

      let calendars = [{ id: 'primary', summary: 'Google Calendar' }]

      try {
        const calendarListResponse = await fetch('https://www.googleapis.com/calendar/v3/users/me/calendarList', {
          headers: {
            Authorization: `Bearer ${tokenResult.accessToken}`
          }
        })

        if (calendarListResponse.ok) {
          const calendarListData = await calendarListResponse.json()
          const visibleCalendars = (calendarListData.items || []).filter((calendar) => !calendar.hidden && calendar.selected !== false)
          if (visibleCalendars.length > 0) {
            calendars = visibleCalendars
          }
        }
      } catch (calendarListError) {
        console.warn('Falling back to primary Google Calendar only:', calendarListError)
      }

      const events = []

      for (const calendar of calendars) {
        const eventResponse = await fetch(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendar.id)}/events?${eventParams.toString()}`, {
          headers: {
            Authorization: `Bearer ${tokenResult.accessToken}`
          }
        })

        if (!eventResponse.ok) {
          continue
        }

        const eventData = await eventResponse.json()
        const calendarEvents = (eventData.items || [])
          .filter((event) => event.status !== 'cancelled')
          .map((event) => {
            const startValue = event.start?.dateTime || event.start?.date
            const endValue = event.end?.dateTime || event.end?.date || startValue
            const start = startValue ? new Date(startValue) : new Date()
            const end = endValue ? new Date(endValue) : new Date(start.getTime() + 30 * 60000)
            const isAllDay = !!event.start?.date && !event.start?.dateTime

            return {
              id: `google-event-${event.id}`,
              externalCalendarId: event.id,
              source: 'google-calendar',
              sourceLabel: calendar.summary || 'Google Calendar',
              calendarId: calendar.id,
              isExternalCalendarItem: true,
              readOnly: true,
              date: (isAllDay ? new Date(`${event.start.date}T00:00:00`) : start).toISOString().split('T')[0],
              time: isAllDay ? 'All day' : start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
              clientName: event.summary || 'Google Calendar Event',
              clientEmail: event.attendees?.[0]?.email || localStorage.getItem('google_calendar_user_email') || '',
              clientPhone: '',
              duration: Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000) || 30),
              notes: event.description || '',
              meetingType: event.eventType === 'outOfOffice' ? 'Out of Office' : 'Google Calendar Event',
              status: event.status || 'confirmed',
              location: event.location || '',
              startTime: start.toISOString(),
              endTime: end.toISOString()
            }
          })

        events.push(...calendarEvents)
      }

      let tasks = []

      try {
        const taskListResponse = await fetch('https://tasks.googleapis.com/tasks/v1/users/@me/lists', {
          headers: {
            Authorization: `Bearer ${tokenResult.accessToken}`
          }
        })

        if (taskListResponse.ok) {
          const taskListData = await taskListResponse.json()
          const taskLists = taskListData.items || []

          for (const taskList of taskLists) {
            const taskParams = new URLSearchParams({
              showCompleted: 'true',
              showHidden: 'true',
              maxResults: '100',
              dueMin: startDate.toISOString(),
              dueMax: endDate.toISOString()
            })

            const taskResponse = await fetch(`https://tasks.googleapis.com/tasks/v1/lists/${taskList.id}/tasks?${taskParams.toString()}`, {
              headers: {
                Authorization: `Bearer ${tokenResult.accessToken}`
              }
            })

            if (!taskResponse.ok) continue

            const taskData = await taskResponse.json()
            const listTasks = (taskData.items || [])
              .filter((task) => !!task.due)
              .map((task) => {
                const dueDate = new Date(task.due)
                return {
                  id: `google-task-${task.id}`,
                  externalCalendarId: task.id,
                  source: 'google-task',
                  sourceLabel: 'Google Task',
                  isExternalCalendarItem: true,
                  readOnly: true,
                  date: dueDate.toISOString().split('T')[0],
                  time: 'Task',
                  clientName: task.title || 'Google Task',
                  clientEmail: localStorage.getItem('google_calendar_user_email') || '',
                  clientPhone: '',
                  duration: 15,
                  notes: task.notes || '',
                  meetingType: 'Google Task',
                  status: task.status === 'completed' ? 'completed' : 'needsAction',
                  location: '',
                  startTime: dueDate.toISOString(),
                  endTime: dueDate.toISOString()
                }
              })

            tasks.push(...listTasks)
          }
        }
      } catch (taskError) {
        console.warn('Google Tasks sync skipped:', taskError)
      }

      localStorage.setItem('google_calendar_last_sync', new Date().toISOString())

      return {
        success: true,
        items: [...events, ...tasks].sort((a, b) => new Date(a.startTime) - new Date(b.startTime)),
        eventCount: events.length,
        taskCount: tasks.length
      }
    } catch (error) {
      console.error('Google Calendar import failed:', error)
      return { success: false, error: error.message, items: [] }
    }
  }

  async getOutlookCalendarItems() {
    try {
      const tokenResult = await this._getMicrosoftAccessToken()

      if (!tokenResult.success) {
        const events = JSON.parse(localStorage.getItem('outlook_events') || '[]').map((event) => {
          const start = new Date(event.start)
          const end = new Date(event.end || event.start)
          return {
            id: `outlook-event-${event.id}`,
            externalCalendarId: event.id,
            source: 'outlook-calendar',
            sourceLabel: 'Outlook Calendar',
            isExternalCalendarItem: true,
            readOnly: true,
            date: start.toISOString().split('T')[0],
            time: start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
            clientName: event.subject || 'Outlook Calendar Event',
            clientEmail: event.attendees?.[0]?.email || localStorage.getItem('microsoft_graph_user_email') || '',
            clientPhone: '',
            duration: Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000) || 30),
            notes: event.body || '',
            meetingType: 'Outlook Calendar Event',
            status: 'confirmed',
            location: event.location || '',
            startTime: start.toISOString(),
            endTime: end.toISOString()
          }
        })

        return { success: true, items: events }
      }

      const response = await fetch('https://graph.microsoft.com/v1.0/me/events?$top=100&$orderby=start/dateTime', {
        headers: {
          Authorization: `Bearer ${tokenResult.accessToken}`
        }
      })

      const result = await response.json()
      if (!response.ok) {
        return { success: false, error: result.error?.message || 'Failed to load Outlook events', items: [] }
      }

      const items = (result.value || []).map((event) => {
        const start = new Date(event.start?.dateTime || new Date())
        const end = new Date(event.end?.dateTime || start)

        return {
          id: `outlook-event-${event.id}`,
          externalCalendarId: event.id,
          source: 'outlook-calendar',
          sourceLabel: 'Outlook Calendar',
          isExternalCalendarItem: true,
          readOnly: true,
          date: start.toISOString().split('T')[0],
          time: start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
          clientName: event.subject || 'Outlook Calendar Event',
          clientEmail: event.attendees?.[0]?.emailAddress?.address || localStorage.getItem('microsoft_graph_user_email') || '',
          clientPhone: '',
          duration: Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000) || 30),
          notes: event.body?.content || '',
          meetingType: 'Outlook Calendar Event',
          status: event.showAs || 'confirmed',
          location: event.location?.displayName || '',
          startTime: start.toISOString(),
          endTime: end.toISOString()
        }
      })

      return { success: true, items }
    } catch (error) {
      console.error('Outlook Calendar import failed:', error)
      return { success: false, error: error.message, items: [] }
    }
  }

  async createGoogleEvent(eventData) {
    try {
      const tokenResult = await this._getGoogleAccessToken()
      if (!tokenResult.success) {
        return { success: false, error: tokenResult.error }
      }

      const eventBody = {
        summary: eventData.title,
        description: eventData.description,
        start: {
          dateTime: eventData.start.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        },
        end: {
          dateTime: eventData.end.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        },
        attendees: (eventData.attendees || []).map(email => ({ email })),
        reminders: {
          useDefault: false,
          overrides: [
            { method: 'email', minutes: 24 * 60 },
            { method: 'email', minutes: 60 }
          ]
        }
      }

      const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenResult.accessToken}`
        },
        body: JSON.stringify(eventBody)
      })

      const result = await response.json()
      if (!response.ok) {
        return { success: false, error: result.error?.message || 'Google Calendar API error' }
      }

      return { success: true, eventId: result.id, event: result }
    } catch (error) {
      console.error('Google Calendar event creation failed:', error)
      return { success: false, error: error.message }
    }
  }

  async createOutlookEvent(eventData) {
    try {
      const tokenResult = await this._getMicrosoftAccessToken()
      if (!tokenResult.success) {
        // Fallback to local simulation if no token is available
        const simulatedEventId = `outlook_event_${Date.now()}`
        const events = JSON.parse(localStorage.getItem('outlook_events') || '[]')
        const newEvent = {
          id: simulatedEventId,
          subject: eventData.title,
          start: eventData.start.toISOString(),
          end: eventData.end.toISOString(),
          attendees: eventData.attendees || [],
          body: eventData.description || '',
          location: eventData.location || 'Online Meeting',
          createdAt: new Date().toISOString()
        }
        events.push(newEvent)
        localStorage.setItem('outlook_events', JSON.stringify(events))
        return { success: true, eventId: simulatedEventId, event: newEvent }
      }

      const payload = {
        subject: eventData.title,
        body: {
          contentType: 'Text',
          content: eventData.description || ''
        },
        start: {
          dateTime: eventData.start.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        },
        end: {
          dateTime: eventData.end.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        },
        attendees: (eventData.attendees || []).map(email => ({
          emailAddress: {
            address: email,
            name: email
          },
          type: 'required'
        }))
      }

      const response = await fetch('https://graph.microsoft.com/v1.0/me/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenResult.accessToken}`
        },
        body: JSON.stringify(payload)
      })

      const result = await response.json()
      if (!response.ok) {
        return { success: false, error: result.error?.message || 'Microsoft Graph API error' }
      }

      return { success: true, eventId: result.id, event: result }
    } catch (error) {
      console.error('Outlook Calendar event creation failed:', error)
      return { success: false, error: error.message }
    }
  }
}

export default new CalendarService();